import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { fromDate, getLocalTimeZone, today } from '@internationalized/date';
import EnButton from '../../base/EnButton/EnButton.vue';
import { PopoverRoot, PopoverTrigger } from 'reka-ui';
import { Calendar as CalendarIcon, X as ClearIcon } from 'lucide-vue-next';
import { dateFormatDay } from 'Js/utils/dateFormat';
import Calendar from 'Components/primitives/calendar/Calendar.vue';
import PopoverContent from 'Components/primitives/popover/PopoverContent.vue';
import EnTextField from '../EnTextField/EnTextField.vue';
/**
 * A text field that provides a datepicker.
 */
export default /*@__PURE__*/ _defineComponent({
    __name: 'EnDatePicker',
    props: /*@__PURE__*/ _mergeModels({
        id: { type: String, required: false },
        isDisabled: { type: Boolean, required: false },
        label: { type: String, required: false },
        placeholder: { type: String, required: false },
        rules: { type: Array, required: false },
        name: { type: String, required: false },
        staticId: { type: [String, Number, null], required: false, default: null },
        persistentHint: { type: Boolean, required: false },
        clearable: { type: Boolean, required: false },
        hint: { type: String, required: false },
        required: { type: Boolean, required: false },
        allowedDates: { type: Function, required: false },
        isMonth: { type: Boolean, required: false, default: false },
        showMonthYearDropdowns: { type: Boolean, required: false, default: true },
        calendarVariant: { type: String, required: false },
        confirmText: { type: String, required: false }
    }, {
        "modelValue": {},
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(['input', 'update:modelValue'], ["update:modelValue"]),
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        const props = __props;
        const { locale } = useI18n();
        const modelValue = _useModel(__props, 'modelValue');
        const emit = __emit;
        const open = ref(false);
        const pickedValue = ref();
        // Tracks which month the calendar displays, syncs to the selected date on open.
        const calendarPage = ref(today(getLocalTimeZone()));
        watch(open, (isOpen) => {
            if (isOpen && calendarValue.value) {
                calendarPage.value = calendarValue.value;
            }
            if (isOpen)
                pickedValue.value = undefined;
        });
        function toDateValue(value) {
            if (!value)
                return undefined;
            const d = new Date(value);
            return isNaN(d.getTime()) ? undefined : fromDate(d, getLocalTimeZone());
        }
        const calendarValue = computed(() => toDateValue(modelValue.value));
        const shownCalendarValue = computed(() => pickedValue.value ?? calendarValue.value);
        const dateFormatted = computed(() => dateFormatDay(modelValue.value, true) || '');
        const labelComputed = computed(() => props.required ? `${props.label} *` : props.label);
        const isDateUnavailable = computed(() => {
            if (!props.allowedDates)
                return undefined;
            return (date) => !props.allowedDates(date.toDate(getLocalTimeZone()));
        });
        function onCalendarSelect(value) {
            if (props.confirmText) {
                pickedValue.value = value;
                return;
            }
            takeOver(value);
        }
        /**
         * Sets the date and closes the calendar.
         */
        function takeOver(value) {
            const jsDate = value ? value.toDate(getLocalTimeZone()) : null;
            emit('input', jsDate);
            emit('update:modelValue', jsDate);
            nextTick(() => { open.value = false; });
        }
        function onClear() {
            modelValue.value = null;
            emit('input', null);
            open.value = false;
        }
        const __returned__ = { props, locale, modelValue, emit, open, pickedValue, calendarPage, toDateValue, calendarValue, shownCalendarValue, dateFormatted, labelComputed, isDateUnavailable, onCalendarSelect, takeOver, onClear, EnButton, get PopoverRoot() { return PopoverRoot; }, get PopoverTrigger() { return PopoverTrigger; }, get CalendarIcon() { return CalendarIcon; }, get ClearIcon() { return ClearIcon; }, Calendar, PopoverContent, EnTextField };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
